import { useEffect, useRef, useState } from "react";

/**
 * Detects whether the referenced element's text is visually truncated.
 *
 * Works for both single-line truncation (`whiteSpace: nowrap` +
 * `textOverflow: ellipsis`) and multi-line clamping (`-webkit-box` +
 * `WebkitLineClamp`), by comparing both height and width of the content
 * against the element's visible box. Re-checks on window resize.
 */
export const useIsTruncated = <T extends HTMLElement = HTMLElement>() => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const el = ref.current;
      if (!el) return;
      setIsTruncated(
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth,
      );
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, []);

  return { ref, isTruncated };
};
